import { OmitStrict, Styled } from '../types';
import { extractRest } from '../utils/object';
import { prefixKeysStyled } from '../utils/styles';

import { BaseFlexProps, extractFlexProps } from './flex.styles';
import { BaseOverflowProps, extractOverflowProps } from './overflow.styles';
import { BaseSizeProps, extractSizeProps } from './size.styles';
import { BaseSpacingProps, extractSpacingProps } from './spacing.styles';
import { BaseTextProps, extractTextProps } from './text.styles';

type BaseProps = BaseFlexProps &
  BaseOverflowProps &
  BaseSizeProps &
  BaseSpacingProps &
  BaseTextProps;

/**
 * Lets you map styling props into prefixed versions used by common styles
 *
 * This could be usefull if you don't wanna pass these props manually.
 *
 * @example
 * const MyStyledComponent = styled.div<StyledFlexProps & StyledSpacingProps>`
 *  ${flexStyles}
 *  ${spacingStyles}
 * `;
 *
 * const SomeComponent: React.FC<FlexProps & SpacingProps & SomeOtherProps> = (props) => {
 *   return <MyStyledComponent {...mapStyledProps(props)}>...</MyStyledComponent>
 * }
 */
export const mapStyledProps = <T extends Partial<BaseProps>>(
  props: T,
): Styled<BaseProps> & OmitStrict<T, keyof BaseProps> => {
  const extracted: BaseProps = {
    ...extractFlexProps(props),
    ...extractOverflowProps(props),
    ...extractSizeProps(props),
    ...extractSpacingProps(props),
    ...extractTextProps(props),
  };

  const rest = extractRest(props, extracted);

  const mapped = prefixKeysStyled(extracted);

  return { ...rest, ...mapped };
};
